import React, { useState, useMemo, useRef, useEffect } from 'react';
import Products2Hero from '../components/Products2Hero';
import './HelpCenterPage.css';

/* ─── Icons ─────────────────────────────────── */
const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const ArrowUpRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="7" y1="17" x2="17" y2="7" />
    <polyline points="7 7 17 7 17 17" />
  </svg>
);

const MailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

/* ─── FAQ Data grouped by category ──────────── */
/* ─── FAQ Data grouped by category ──────────── */
const FAQ_CATEGORIES = [
  {
    id: 'general',
    name: 'General',
    faqs: [
      {
        q: "What does 'Certified Refurbished' mean at Nexawave?",
        a: "Every device undergoes a rigorous 50-point quality audit by our certified engineers. We don't just clean them; we restore them to peak performance, replacing any components that don't meet our enterprise-grade standards. You get laptop that looks and performs like new, but at a fraction of the cost."
      },
      {
        q: "Are the laptops genuine and original?",
        a: "Absolutely. We exclusively source from corporate lease returns and verified enterprise channels. Every laptop is 100% original, featuring genuine Intel/AMD processors and magnesium-alloy chassis common in business-class machines."
      },
      {
        q: "Why are your prices so much lower than retail?",
        a: "By sourcing premium enterprise laptops that have completed their initial corporate lease, we can offer professional-grade hardware (originally retailing for ₹1L+) at up to 70% off. You're paying for the performance, not the 'new box' premium."
      }
    ]
  },
  {
    id: 'warranty',
    name: 'Warranty & Returns',
    faqs: [
      {
        q: "What is covered under the 12-month warranty?",
        a: "Our comprehensive warranty covers all hardware malfunctions and manufacturing defects. If your screen, keyboard, motherboard, or internal components fail through normal use, we'll repair or replace it at zero cost to you. We stand behind our 50-point audit."
      },
      {
        q: "How do I claim my warranty?",
        a: "It's simple. Contact our Hyderabad hub via WhatsApp or Email with your Order ID. We provide doorstep pickup in most major cities or you can visit our service center directly for instant resolution."
      },
      {
        q: "What is your return policy?",
        a: "We offer a 7-day 'No Questions Asked' return policy. If you aren't completely satisfied with the condition or performance of your device, simply return it in its original packaging for a full refund or exchange."
      }
    ]
  },
  {
    id: 'shipping',
    name: 'Shipping & Delivery',
    faqs: [
      {
        q: "How long does delivery take?",
        a: "We ship all orders within 48 hours of verification. Delivery typically takes 3-5 business days depending on your location. Customers in Hyderabad often receive their devices within 24-48 hours."
      },
      {
        q: "Is the shipping insured?",
        a: "Yes, every Nexawave shipment is fully insured. In the extremely rare event of transit damage, we handle the claim and send you a replacement immediately. You are 100% protected."
      },
      {
        q: "Can I track my order in real-time?",
        a: "Once shipped, you will receive a tracking link via SMS and Email. You can also track your status directly on our website through the 'My Orders' section."
      }
    ]
  },
  {
    id: 'payments',
    name: 'Payments',
    faqs: [
      {
        q: "What payment methods do you accept?",
        a: "We accept all major Credit/Debit cards, UPI (GPay, PhonePe, Paytm), and Net Banking via our secure Razorpay gateway. We also offer No-Cost EMI options on eligible cards."
      },
      {
        q: "Is Cash on Delivery (COD) available?",
        a: "Yes, we offer COD for most pin codes across India. To prevent fraudulent orders, we may require a small commitment advance for high-value workstation laptops, which is adjusted against your final billing."
      },
      {
        q: "Is it safe to pay online on Nexawave?",
        a: "Completely. We use industry-standard SSL encryption and partner with Razorpay, India's leading payment gateway, to ensure your financial data is never stored and always protected."
      }
    ]
  }
];

/* ─── Component ─────────────────────────────── */
const HelpCenterPage = () => {
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [openId, setOpenId] = useState(null);
  const searchRef = useRef(null);

  /* Flatten + filter */
  const filteredFaqs = useMemo(() => {
    const flat = FAQ_CATEGORIES.flatMap(cat =>
      cat.faqs.map((item, i) => ({
        ...item,
        category: cat.name,
        categoryId: cat.id,
        id: `${cat.id}-${i}`
      }))
    );

    return flat.filter(item => {
      const matchesCategory =
        activeCategory === 'all' || item.categoryId === activeCategory;
      const matchesQuery =
        !query ||
        item.q.toLowerCase().includes(query.toLowerCase()) ||
        item.a.toLowerCase().includes(query.toLowerCase());
      return matchesCategory && matchesQuery;
    });
  }, [query, activeCategory]);

  /* Keyboard: "/" to focus search */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && document.activeElement.tagName !== 'INPUT') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="hc-page">
      <Products2Hero
        className="hero-tight"
        tagline="NEXAWAVE SUPPORT · 24/7 ASSISTANCE · HYDERABAD OPERATIONAL HUB"
        title1="Help Center"
        title2=""
        title3=""
        description=""
        button1Text="Browse FAQs"
        button1Link="#hc-faqs"
        button2Text="Contact Support"
        button2Link="/contact-us"
      />

      <section className="hc-section">
        <div className="hc-container">

          {/* Search bar */}
          <div className="hc-search-wrap">
            <div className="hc-search">
              <SearchIcon />
              <input
                ref={searchRef}
                type="text"
                className="hc-search-input"
                placeholder="Search for answers..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search FAQs"
              />
              <kbd className="hc-kbd">/</kbd>
            </div>
          </div>

          {/* Intro */}
          <header className="hc-intro" id="hc-faqs">
            <p className="hc-kicker">Frequently asked</p>
            <h2 className="hc-title">
              Answers, organized.<br />
              <span>Pick a topic or search above.</span>
            </h2>
          </header>

          {/* Category tabs */}
          <div className="hc-tabs" role="tablist" aria-label="FAQ categories">
            <button
              className={`hc-tab ${activeCategory === 'all' ? 'is-active' : ''}`}
              onClick={() => setActiveCategory('all')}
              role="tab"
              aria-selected={activeCategory === 'all'}
            >
              All
              <span className="hc-tab-count">
                {FAQ_CATEGORIES.reduce((s, c) => s + c.faqs.length, 0)}
              </span>
            </button>
            {FAQ_CATEGORIES.map(cat => (
              <button
                key={cat.id}
                className={`hc-tab ${activeCategory === cat.id ? 'is-active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
                role="tab"
                aria-selected={activeCategory === cat.id}
              >
                {cat.name}
                <span className="hc-tab-count">{cat.faqs.length}</span>
              </button>
            ))}
          </div>

          {/* FAQ List */}
          <div className="hc-faq-list">
            {filteredFaqs.length === 0 ? (
              <div className="hc-empty">
                <p className="hc-empty-title">No results for "{query}"</p>
                <p className="hc-empty-sub">
                  Try different keywords or <a href="/contact-us">contact our team</a>.
                </p>
              </div>
            ) : (
              filteredFaqs.map((item) => {
                const isOpen = openId === item.id;
                return (
                  <article
                    key={item.id}
                    className={`hc-faq ${isOpen ? 'is-open' : ''}`}
                  >
                    <button
                      className="hc-faq-btn"
                      onClick={() => setOpenId(isOpen ? null : item.id)}
                      aria-expanded={isOpen}
                      aria-controls={`faq-panel-${item.id}`}
                    >
                      <div className="hc-faq-head">
                        <span className="hc-faq-cat">{item.category}</span>
                        <h3 className="hc-faq-q">{item.q}</h3>
                      </div>
                      <span className="hc-faq-icon" aria-hidden="true">
                        <PlusIcon />
                      </span>
                    </button>
                    <div
                      className="hc-faq-panel"
                      id={`faq-panel-${item.id}`}
                      role="region"
                      hidden={!isOpen}
                    >
                      <div className="hc-faq-body">
                        <p>{item.a}</p>
                      </div>
                    </div>
                  </article>
                );
              })
            )}
          </div>

          {/* Still need help — CTA row */}
          <footer className="hc-cta">
            <div className="hc-cta-text">
              <p className="hc-kicker">Still stuck?</p>
              <h3 className="hc-cta-title">
                Our team replies within one business day.
              </h3>
            </div>

            <div className="hc-cta-actions">
              <a className="hc-cta-btn hc-cta-btn--primary" href="mailto:support@nexawavetechnology.com">
                <MailIcon />
                <span>Email support</span>
                <ArrowUpRight />
              </a>
              <a className="hc-cta-btn" href="tel:+916303561201">
                <PhoneIcon />
                <span>+91 63035 61201</span>
              </a>
            </div>
          </footer>

        </div>
      </section>
    </div>
  );
};

export default HelpCenterPage;